import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Grid, 
    Header, 
    Statistic, 
    Progress, 
    Table, 
    Icon, 
    Label, 
    Segment,
    Tab,
    Button,
    Message,
    Dimmer,
    Loader
} from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';

function DijonMarkets() {
    const [marketsData, setMarketsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const loadMarketsData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/markets/dashboard/');
            setMarketsData(response.data);
            setLastUpdated(new Date().toLocaleString());
            setError(null);
            // Return true if we have actual data
            const hasData = response.data?.markets?.length > 0 || response.data?.employment?.length > 0;
            return hasData;
        } catch (err) {
            console.error('Error loading markets data:', err);
            setError('Failed to load markets data');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        try {
            setLoading(true);
            // Trigger data collection
            await axiosInstance.post('/markets/collect_market_data/');
            // Reload dashboard data
            await loadMarketsData();
        } catch (err) {
            console.error('Error refreshing data:', err);
            setError('Failed to refresh data');
        }
    };

    useEffect(() => {
        // Load data, and if empty, trigger a collection first
        const init = async () => {
            const res = await loadMarketsData();
            // If no market data came back, auto-collect
            if (!res) {
                try {
                    await axiosInstance.post('/markets/collect_market_data/');
                    await loadMarketsData();
                } catch (e) {
                    console.error('Auto-collect failed:', e);
                }
            }
        };
        init();
        // Auto-refresh display every 5 minutes
        const interval = setInterval(loadMarketsData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const formatNumber = (value, decimals = 2) => {
        if (!value) return 'N/A';
        return parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const formatCurrency = (value) => {
        if (!value) return 'N/A';
        return `$${formatNumber(value)}`;
    };

    const formatPercent = (value) => {
        if (!value) return 'N/A';
        const num = parseFloat(value);
        return `${num >= 0 ? '+' : ''}${formatNumber(num)}%`;
    };

    const getChangeColor = (value) => {
        if (!value) return 'grey';
        const num = parseFloat(value);
        return num >= 0 ? 'green' : 'red';
    };

    const getSentimentColor = (score) => {
        if (!score) return 'grey';
        const num = parseFloat(score);
        if (num >= 0.2) return 'green';
        if (num <= -0.2) return 'red';
        return 'yellow';
    };

    const renderEmploymentTab = () => {
        if (!marketsData?.employment) return <div>No employment data available</div>;

        const keyMetrics = {
            payrolls: marketsData.employment.find(e => e.series_id === 'PAYEMS'),
            adp: marketsData.employment.find(e => e.series_id === 'NPPTTL'),
            unemployment: marketsData.employment.find(e => e.series_id === 'UNRATE'),
            participation: marketsData.employment.find(e => e.series_id === 'CIVPART'),
            earnings: marketsData.employment.find(e => e.series_id === 'AHETPI')
        };

        return (
            <div>
                <Grid columns={3} stackable>
                    <Grid.Column>
                        <Statistic color="blue">
                            <Statistic.Value>{keyMetrics.payrolls ? formatNumber(keyMetrics.payrolls.value / 1000, 1) + 'M' : 'N/A'}</Statistic.Value>
                            <Statistic.Label>Total Jobs</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                        <Statistic color={keyMetrics.unemployment ? (parseFloat(keyMetrics.unemployment.value) < 4 ? 'green' : 'orange') : 'grey'}>
                            <Statistic.Value>{keyMetrics.unemployment ? keyMetrics.unemployment.value + '%' : 'N/A'}</Statistic.Value>
                            <Statistic.Label>Unemployment Rate</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                        <Statistic color="teal">
                            <Statistic.Value>{keyMetrics.earnings ? formatCurrency(keyMetrics.earnings.value) : 'N/A'}</Statistic.Value>
                            <Statistic.Label>Avg Hourly Earnings</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                </Grid>

                <Segment>
                    <Header as="h4">
                        <Icon name="chart line" />
                        Employment Indicators
                    </Header>
                    <Table celled striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Indicator</Table.HeaderCell>
                                <Table.HeaderCell>Value</Table.HeaderCell>
                                <Table.HeaderCell>Unit</Table.HeaderCell>
                                <Table.HeaderCell>Source</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {marketsData.employment.map((item, index) => (
                                <Table.Row key={index}>
                                    <Table.Cell>
                                        <Header as="h5" style={{margin: 0}}>
                                            {item.series_name}
                                        </Header>
                                        <small style={{color: 'grey'}}>{item.series_id}</small>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <strong style={{fontSize: '1.1em'}}>
                                            {formatNumber(item.value)}
                                        </strong>
                                    </Table.Cell>
                                    <Table.Cell>{item.unit}</Table.Cell>
                                    <Table.Cell>
                                        <Label color="blue" size="mini">
                                            {item.source.toUpperCase()}
                                        </Label>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Segment>
            </div>
        );
    };

    const renderFedPolicyTab = () => {
        if (!marketsData?.federal_reserve) return <div>No Fed data available</div>;

        const fedFunds = marketsData.federal_reserve.find(f => f.series_id === 'FEDFUNDS');
        const treasury10yr = marketsData.federal_reserve.find(f => f.series_id === 'DGS10');
        const treasury2yr = marketsData.federal_reserve.find(f => f.series_id === 'DGS2');

        return (
            <div>
                <Grid columns={3} stackable>
                    <Grid.Column>
                        <Card>
                            <Card.Content textAlign="center">
                                <Card.Header>Fed Funds Rate</Card.Header>
                                <div style={{fontSize: '2em', fontWeight: 'bold', color: '#2185d0', margin: '10px 0'}}>
                                    {fedFunds ? fedFunds.value + '%' : 'N/A'}
                                </div>
                                {fedFunds?.target_range && (
                                    <div style={{fontSize: '0.9em', color: 'grey'}}>
                                        Target Range: {fedFunds.target_range}
                                    </div>
                                )}
                                {fedFunds?.fomc_decision && (
                                    <Label color="blue" size="small">
                                        {fedFunds.fomc_decision}
                                    </Label>
                                )}
                            </Card.Content>
                        </Card>
                    </Grid.Column>
                    <Grid.Column>
                        <Card>
                            <Card.Content textAlign="center">
                                <Card.Header>2-Year Treasury</Card.Header>
                                <div style={{fontSize: '2em', fontWeight: 'bold', color: '#00b5ad', margin: '10px 0'}}>
                                    {treasury2yr ? treasury2yr.value + '%' : 'N/A'}
                                </div>
                                <div style={{fontSize: '0.9em', color: 'grey'}}>
                                    Short-term benchmark
                                </div>
                            </Card.Content>
                        </Card>
                    </Grid.Column>
                    <Grid.Column>
                        <Card>
                            <Card.Content textAlign="center">
                                <Card.Header>10-Year Treasury</Card.Header>
                                <div style={{fontSize: '2em', fontWeight: 'bold', color: '#a333c8', margin: '10px 0'}}>
                                    {treasury10yr ? treasury10yr.value + '%' : 'N/A'}
                                </div>
                                <div style={{fontSize: '0.9em', color: 'grey'}}>
                                    Long-term benchmark
                                </div>
                            </Card.Content>
                        </Card>
                    </Grid.Column>
                </Grid>

                <Segment>
                    <Header as="h4">
                        <Icon name="university" />
                        Yield Curve
                    </Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Maturity</Table.HeaderCell>
                                <Table.HeaderCell>Rate</Table.HeaderCell>
                                <Table.HeaderCell>Series</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {marketsData.federal_reserve.map((item, index) => (
                                <Table.Row key={index}>
                                    <Table.Cell>
                                        <strong>{item.series_name.replace(' Constant Maturity Rate', '').replace('Federal Funds Rate', 'Fed Funds')}</strong>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{fontSize: '1.2em', fontWeight: 'bold'}}>
                                            {item.value}%
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <code style={{fontSize: '0.9em'}}>{item.series_id}</code>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                    <div style={{marginTop: '10px', fontSize: '0.85em', color: 'grey', textAlign: 'right'}}>
                        <Icon name="info circle" /> Source: Federal Reserve Economic Data (FRED), St. Louis Fed
                    </div>
                </Segment>
            </div>
        );
    };

    const renderMarketsTab = () => {
        if (!marketsData?.markets) return <div>No market data available</div>;

        return (
            <div>
                <Grid columns={3} stackable>
                    {marketsData.markets.slice(0, 6).map((market, index) => (
                        <Grid.Column key={index}>
                            <Card>
                                <Card.Content>
                                    <Card.Header style={{display: 'flex', justifyContent: 'space-between'}}>
                                        <span>{market.symbol}</span>
                                        <Label 
                                            color={getChangeColor(market.change_percent)} 
                                            size="small"
                                        >
                                            {formatPercent(market.change_percent)}
                                        </Label>
                                    </Card.Header>
                                    <Card.Meta>{market.name}</Card.Meta>
                                    <Card.Description>
                                        <div style={{fontSize: '1.5em', fontWeight: 'bold', margin: '8px 0'}}>
                                            {formatCurrency(market.price)}
                                        </div>
                                        <div style={{
                                            color: getChangeColor(market.change),
                                            fontWeight: 'bold'
                                        }}>
                                            {market.change && parseFloat(market.change) >= 0 ? '+' : ''}{formatNumber(market.change)}
                                        </div>
                                        <div style={{fontSize: '0.9em', color: 'grey', marginTop: '5px'}}>
                                            Vol: {market.volume ? (parseFloat(market.volume) / 1000000).toFixed(1) + 'M' : 'N/A'}
                                        </div>
                                    </Card.Description>
                                </Card.Content>
                                <Card.Content extra>
                                    <Label size="mini" color="blue">
                                        <Icon name="database" /> Yahoo Finance
                                    </Label>
                                    {market.date_value && (
                                        <span style={{float: 'right', fontSize: '0.8em', color: 'grey'}}>
                                            {market.date_value}
                                        </span>
                                    )}
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    ))}
                </Grid>
                <div style={{marginTop: '10px', fontSize: '0.85em', color: 'grey', textAlign: 'right'}}>
                    <Icon name="info circle" /> Data from Yahoo Finance via yfinance. Refreshed every 4 hours.
                </div>
            </div>
        );
    };

    const renderEconomicTab = () => {
        if (!marketsData?.economic_indicators) return <div>No economic data available</div>;

        const gdpData = marketsData.economic_indicators.find(e => e.series_id === 'GDP');
        const cpiData = marketsData.economic_indicators.find(e => e.series_id === 'CPIAUCSL');
        const consumerData = marketsData.economic_indicators.find(e => e.series_id === 'UMCSENT');

        return (
            <div>
                <Grid columns={3} stackable>
                    <Grid.Column>
                        <Statistic color="green">
                            <Statistic.Value>{gdpData ? '$' + formatNumber(gdpData.value / 1000, 1) + 'T' : 'N/A'}</Statistic.Value>
                            <Statistic.Label>GDP (Trillions)</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                        <Statistic color="orange">
                            <Statistic.Value>{cpiData ? formatNumber(cpiData.value, 1) : 'N/A'}</Statistic.Value>
                            <Statistic.Label>CPI Index</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                        <Statistic color={consumerData ? (parseFloat(consumerData.value) > 70 ? 'green' : 'orange') : 'grey'}>
                            <Statistic.Value>{consumerData ? formatNumber(consumerData.value, 1) : 'N/A'}</Statistic.Value>
                            <Statistic.Label>Consumer Sentiment</Statistic.Label>
                        </Statistic>
                    </Grid.Column>
                </Grid>

                <Segment>
                    <Header as="h4">
                        <Icon name="chart bar" />
                        Economic Indicators
                    </Header>
                    <Table celled striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Indicator</Table.HeaderCell>
                                <Table.HeaderCell>Value</Table.HeaderCell>
                                <Table.HeaderCell>Category</Table.HeaderCell>
                                <Table.HeaderCell>Frequency</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {marketsData.economic_indicators.map((item, index) => (
                                <Table.Row key={index}>
                                    <Table.Cell>
                                        <Header as="h5" style={{margin: 0}}>
                                            {item.series_name}
                                        </Header>
                                        <small style={{color: 'grey'}}>{item.series_id}</small>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <strong style={{fontSize: '1.1em'}}>
                                            {formatNumber(item.value)}
                                        </strong>
                                        <div style={{fontSize: '0.8em', color: 'grey'}}>
                                            {item.unit}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Label color="teal" size="mini">
                                            {item.category.replace('_', ' ').toUpperCase()}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell>{item.frequency}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                    <div style={{marginTop: '10px', fontSize: '0.85em', color: 'grey', textAlign: 'right'}}>
                        <Icon name="info circle" /> Source: FRED (St. Louis Fed). Refreshed every 4 hours.
                    </div>
                </Segment>
            </div>
        );
    };

    const renderSentimentTab = () => {
        if (!marketsData?.sentiment) return <div>No sentiment data available</div>;

        const overallSentiment = marketsData.sentiment.find(s => s.category === 'overall');

        return (
            <div>
                {overallSentiment && (
                    <Card fluid>
                        <Card.Content textAlign="center">
                            <Card.Header>Overall Market Sentiment</Card.Header>
                            <div style={{
                                fontSize: '3em', 
                                fontWeight: 'bold', 
                                color: getSentimentColor(overallSentiment.sentiment_score) === 'green' ? '#21ba45' : 
                                       getSentimentColor(overallSentiment.sentiment_score) === 'red' ? '#db2828' : '#fbbd08',
                                margin: '15px 0'
                            }}>
                                {overallSentiment.sentiment_description}
                            </div>
                            <div style={{fontSize: '1.2em', margin: '10px 0'}}>
                                Score: {formatNumber(overallSentiment.sentiment_score)} 
                                ({formatNumber(overallSentiment.confidence_level * 100)}% confidence)
                            </div>
                            <Progress 
                                percent={Math.abs(parseFloat(overallSentiment.sentiment_score)) * 100} 
                                color={getSentimentColor(overallSentiment.sentiment_score)}
                                style={{margin: '15px 0'}}
                            />
                        </Card.Content>
                    </Card>
                )}

                <Grid columns={2} stackable>
                    {marketsData.sentiment.filter(s => s.category !== 'overall').map((sentiment, index) => (
                        <Grid.Column key={index}>
                            <Card>
                                <Card.Content>
                                    <Card.Header style={{display: 'flex', justifyContent: 'space-between'}}>
                                        <span>{sentiment.category.replace('_', ' ').toUpperCase()}</span>
                                        <Label 
                                            color={getSentimentColor(sentiment.sentiment_score)} 
                                            size="small"
                                        >
                                            {sentiment.sentiment_description}
                                        </Label>
                                    </Card.Header>
                                    <Card.Description>
                                        <div style={{margin: '10px 0'}}>
                                            <strong>Score: {formatNumber(sentiment.sentiment_score)}</strong>
                                        </div>
                                        <Progress 
                                            percent={Math.abs(parseFloat(sentiment.sentiment_score)) * 100} 
                                            color={getSentimentColor(sentiment.sentiment_score)}
                                            size="small"
                                        />
                                        {sentiment.signal_summary && (
                                            <div style={{fontSize: '0.9em', marginTop: '8px'}}>
                                                <Icon name="thumbs up" color="green" />
                                                {sentiment.signal_summary.positive_percent}% positive
                                                <br />
                                                <Icon name="thumbs down" color="red" />
                                                {sentiment.signal_summary.negative_percent}% negative
                                            </div>
                                        )}
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    ))}
                </Grid>

                {overallSentiment?.data_sources?.sources && (
                    <div style={{marginTop: '15px', fontSize: '0.85em', color: 'grey'}}>
                        <Icon name="info circle" /> Sources: {overallSentiment.data_sources.sources.join(', ')}
                        {overallSentiment.key_factors?.length > 0 && (
                            <div style={{marginTop: '5px'}}>
                                Key factors: {overallSentiment.key_factors.join(' | ')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const panes = [
        {
            menuItem: { key: 'employment', icon: 'users', content: 'Employment' },
            render: () => <Tab.Pane>{renderEmploymentTab()}</Tab.Pane>
        },
        {
            menuItem: { key: 'fed', icon: 'university', content: 'Fed Policy' },
            render: () => <Tab.Pane>{renderFedPolicyTab()}</Tab.Pane>
        },
        {
            menuItem: { key: 'markets', icon: 'chart line', content: 'Markets' },
            render: () => <Tab.Pane>{renderMarketsTab()}</Tab.Pane>
        },
        {
            menuItem: { key: 'economic', icon: 'chart bar', content: 'Economic' },
            render: () => <Tab.Pane>{renderEconomicTab()}</Tab.Pane>
        },
        {
            menuItem: { key: 'sentiment', icon: 'heart', content: 'Sentiment' },
            render: () => <Tab.Pane>{renderSentimentTab()}</Tab.Pane>
        }
    ];

    if (loading && !marketsData) {
        return (
            <Card fluid>
                <Dimmer active>
                    <Loader>Loading Markets Data...</Loader>
                </Dimmer>
                <Card.Content style={{minHeight: '400px'}} />
            </Card>
        );
    }

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div>
                        <Icon name="chart area" />
                        Markets Dashboard
                    </div>
                    <div>
                        <Button 
                            icon="refresh" 
                            content="Refresh" 
                            onClick={refreshData}
                            loading={loading}
                            size="small"
                        />
                    </div>
                </Card.Header>
                
                {error && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                {lastUpdated && (
                    <div style={{fontSize: '0.9em', color: 'grey', marginBottom: '15px'}}>
                        Last updated: {lastUpdated}
                    </div>
                )}

                <Tab 
                    panes={panes} 
                    defaultActiveIndex={0}
                    menu={{ secondary: true, pointing: true }}
                />
            </Card.Content>
        </Card>
    );
}

export default DijonMarkets;